import React, { useEffect, useState } from "react";
import {
  Document,
  Page,
  PDFViewer,
  Text,
  Image,
  View,
  pdf,
} from "@react-pdf/renderer";
import { overviewTemplateStyles } from "./invoiceStyles";
import logoSm from "../../../assets/images/Elebert-Logo.png"; // fallback logo
import { CustomButton } from "components/CommonInputs/CustomButtons/CustomButton";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload } from "@fortawesome/free-solid-svg-icons";
import { get, uri } from "helpers/api_helper";

// Background page wrapper
const PageWithBackground = ({ children }) => (
  <Page size="A4" style={overviewTemplateStyles.page}>
    <View
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        zIndex: -1,
        justifyContent: "center",
        alignItems: "center",
      }}
      fixed
    ></View>
    <View style={overviewTemplateStyles.pageContent}>{children}</View>
  </Page>
);

// Date formatter
const formatDate = (dateString) => {
  if (!dateString || !/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    return "N/A";
  }
  const [year, month, day] = dateString.split("-");
  return `${day}-${month}-${year}`;
};

// Main PDF document
const InvoiceOverviewDocument = ({ invoiceData, logoBase64 }) => {
  const calculateSubtotal = () => {
    return (
      invoiceData.lineItems?.reduce(
        (total, item) => total + (item.subtotal || 0),
        0
      ) || 0
    );
  };

  const calculateTax1 = () => {
    return (
      invoiceData.lineItems?.reduce(
        (total, item) => total + (item.taxAmount1 || 0),
        0
      ) || 0
    );
  };

  const calculateTax2 = () => {
    return (
      invoiceData.lineItems?.reduce(
        (total, item) => total + (item.taxAmount2 || 0),
        0
      ) || 0
    );
  };

  const hasTax1 = invoiceData.lineItems?.some(
    (item) => item.taxType1 !== "" && item.taxType1 !== 0
  );
  const hasTax2 = invoiceData.lineItems?.some(
    (item) => item.taxType2 !== "" && item.taxType2 !== 0
  );

  return (
    <Document title={invoiceData.invoiceCode || "N/A"}>
      <PageWithBackground>
        <View
          style={{
            ...overviewTemplateStyles.headerRight,
            width: "100%",
            display: "flex",
            alignItems: "flex-end",
          }}
        >
          <View style={overviewTemplateStyles.logoContainer}>
            <Image src={logoBase64 || logoSm} style={{ width: "150px" }} />
          </View>
        </View>

        <Text style={overviewTemplateStyles.invoiceTitle}>INVOICE</Text>
        <View style={overviewTemplateStyles.headerSection}>
          <View style={overviewTemplateStyles.headerLeft}>
            <View style={overviewTemplateStyles.fromSection}>
              <Text style={overviewTemplateStyles.sectionTitle}>FROM:</Text>
              <Text style={overviewTemplateStyles.companyName}>
                {invoiceData.invoiceFromCustomer?.companyName || "N/A"}
              </Text>
              <Text style={overviewTemplateStyles.addressText}>
                {invoiceData.invoiceFromCustomer?.address || "N/A"}
              </Text>
            </View>

            <View style={overviewTemplateStyles.toSection}>
              <Text style={overviewTemplateStyles.sectionTitle}>TO:</Text>
              <Text style={overviewTemplateStyles.companyName}>
                {invoiceData.invoiceToCustomer?.companyName || "N/A"}
              </Text>
              <Text style={overviewTemplateStyles.addressText}>
                {invoiceData.invoiceToCustomer?.address || "N/A"}
              </Text>
            </View>
          </View>

          <View style={overviewTemplateStyles.headerRight}>
            <View style={overviewTemplateStyles.fromSection}>
              <View
                style={{
                  ...overviewTemplateStyles.invoiceDetails,
                  alignItems: "flex-start",
                }}
              >
                <View style={overviewTemplateStyles.summaryRowData}>
                  <Text style={overviewTemplateStyles.summaryLabel}>
                    Invoice #:
                  </Text>
                  <Text style={overviewTemplateStyles.summaryAmount}>
                    {invoiceData.invoiceCode || "N/A"}
                  </Text>
                </View>
                <View style={overviewTemplateStyles.summaryRowData}>
                  <Text style={overviewTemplateStyles.summaryLabel}>
                    Invoice Date:
                  </Text>
                  <Text style={overviewTemplateStyles.summaryAmount}>
                    {formatDate(invoiceData.invoiceDate)}
                  </Text>
                </View>
                <View style={overviewTemplateStyles.summaryRowData}>
                  <Text style={overviewTemplateStyles.summaryLabel}>
                    Due Date:
                  </Text>
                  <Text style={overviewTemplateStyles.summaryAmount}>
                    {formatDate(invoiceData.dueDate)}
                  </Text>
                </View>
              </View>
            </View>
            <View style={overviewTemplateStyles.toSection}>
              <View
                style={{
                  ...overviewTemplateStyles.invoiceDetails,
                  alignItems: "flex-start",
                }}
              >
                {invoiceData.poNumber && (
                  <View style={overviewTemplateStyles.summaryRowData}>
                    <Text style={overviewTemplateStyles.summaryLabel}>
                      PO Number:
                    </Text>
                    <Text style={overviewTemplateStyles.summaryAmount}>
                      {invoiceData.poNumber || "N/A"}
                    </Text>
                  </View>
                )}
              </View>
            </View>
          </View>
        </View>

        {invoiceData.lineItems && invoiceData.lineItems.length > 0 && (
          <View style={overviewTemplateStyles.serviceSection}>
            <View style={overviewTemplateStyles.table}>
              <View style={overviewTemplateStyles.tableHeader}>
                <Text style={overviewTemplateStyles.tableHeaderCell}>
                  Description
                </Text>
                <Text style={overviewTemplateStyles.tableHeaderCellSmall}>
                  Unit Price
                </Text>
                <Text style={overviewTemplateStyles.tableHeaderCellSmall}>
                  Quantity
                </Text>
                {hasTax1 && (
                  <Text style={overviewTemplateStyles.tableHeaderCellSmall}>
                    Tax1(%)
                  </Text>
                )}
                {hasTax2 && (
                  <Text style={overviewTemplateStyles.tableHeaderCellSmall}>
                    Tax2(%)
                  </Text>
                )}
                <Text style={overviewTemplateStyles.tableHeaderCellSmall}>
                  Amount
                </Text>
              </View>

              {invoiceData.lineItems.map((item) => (
                <View key={item.id} style={overviewTemplateStyles.tableRow}>
                  <Text style={overviewTemplateStyles.tableCell}>
                    {item.description || "N/A"}
                  </Text>
                  <Text style={overviewTemplateStyles.tableCellSmall}>
                    {(item.unitPrice ?? 0).toFixed(2)}
                  </Text>
                  <Text style={overviewTemplateStyles.tableCellSmall}>
                    {(item.quantity ?? 0).toFixed(2)}
                  </Text>
                  {hasTax1 && (
                    <Text style={overviewTemplateStyles.tableCellSmall}>
                      {item.taxType1 ?? 0}
                    </Text>
                  )}
                  {hasTax2 && (
                    <Text style={overviewTemplateStyles.tableCellSmall}>
                      {item.taxType2 ?? 0}
                    </Text>
                  )}
                  <Text style={overviewTemplateStyles.tableCellSmall}>
                    {(item.subtotal ?? 0).toFixed(2)}
                  </Text>
                </View>
              ))}
            </View>
          </View>
        )}

        <View style={overviewTemplateStyles.summarySection} wrap={false}>
          <View style={overviewTemplateStyles.summaryRow}>
            <Text style={overviewTemplateStyles.summaryLabel1}>Subtotal</Text>
            <Text style={overviewTemplateStyles.summaryAmount}>
              <Text>{invoiceData.currency || "USD"} </Text>
              <Text>{calculateSubtotal().toFixed(2)}</Text>
            </Text>
          </View>
          {invoiceData.lineItems &&
            invoiceData.lineItems.length > 0 &&
            hasTax1 && (
              <View style={overviewTemplateStyles.summaryRow}>
                <Text style={overviewTemplateStyles.summaryLabel1}>Tax1</Text>
                <Text style={overviewTemplateStyles.summaryAmount}>
                  <Text>{invoiceData.currency || ""} </Text>
                  <Text>{calculateTax1().toFixed(2)}</Text>
                </Text>
              </View>
            )}
          {invoiceData.lineItems &&
            invoiceData.lineItems.length > 0 &&
            hasTax2 && (
              <View style={overviewTemplateStyles.summaryRow}>
                <Text style={overviewTemplateStyles.summaryLabel1}>Tax2</Text>
                <Text style={overviewTemplateStyles.summaryAmount}>
                  <Text>{invoiceData.currency || ""} </Text>
                  <Text>{calculateTax2().toFixed(2)}</Text>
                </Text>
              </View>
            )}
          {(invoiceData.advanceAmount ?? 0) > 0 && (
            <View style={overviewTemplateStyles.summaryRow}>
              <Text style={overviewTemplateStyles.summaryLabel1}>
                Amount Paid
              </Text>
              <Text style={overviewTemplateStyles.summaryAmount}>
                <Text>{invoiceData.currency || "USD"} </Text>
                <Text>{(invoiceData.advanceAmount ?? 0).toFixed(2)}</Text>
              </Text>
            </View>
          )}
          <View style={overviewTemplateStyles.summaryRow}>
            <Text style={overviewTemplateStyles.summaryLabel1}>
              Balance Due
            </Text>
            <Text style={overviewTemplateStyles.summaryAmount}>
              <Text>{invoiceData.currency || "USD"} </Text>
              <Text>
                {(invoiceData.total - (invoiceData.advanceAmount ?? 0)).toFixed(
                  2
                )}
              </Text>
            </Text>
          </View>
        </View>

        <View style={overviewTemplateStyles.notesSection} wrap={false}>
          <Text style={overviewTemplateStyles.notesTitle}>NOTES:</Text>
          <Text style={overviewTemplateStyles.notesText}>
            {invoiceData.notes || "N/A"}
          </Text>
          {invoiceData.comments && (
            <Text style={overviewTemplateStyles.notesText}>
              Comments: {invoiceData.comments}
            </Text>
          )}
          {invoiceData.managerName && (
            <Text style={overviewTemplateStyles.notesText}>
              Manager: {invoiceData.managerName}
            </Text>
          )}
        </View>
      </PageWithBackground>
    </Document>
  );
};

// Wrapper component that fetches logo
const InvoiceOverviewTemplate = ({ invoiceData }) => {
  const [logoBase64, setLogoBase64] = useState(null);

 useEffect(() => {
  const fetchLogo = async () => {
    const logoId = parseInt(invoiceData?.logo ?? 0, 10);
    if (!logoId) return;

    try {
      debugger
      const res = await get(`${uri}/fileservice/api/files/view/${logoId}`);

      console.log("Logo API Raw Response:", res);

      // If your get() returns { data: {...} }
      const fileData = res?.data ?? res; // fallback if data not present

      if (!fileData?.fileContent) {
        console.error("No fileContent found in response");
        return;
      }

      const mimeType = fileData.fileName.endsWith(".jpg") || fileData.fileName.endsWith(".jpeg")
        ? "image/jpeg"
        : "image/png";

      const base64Image = `data:${mimeType};base64,${fileData.fileContent}`;
      setLogoBase64(base64Image);
    } catch (error) {
      console.error("Error fetching logo:", error);
    }
  };

  fetchLogo();
}, [invoiceData?.logo]);

  const handleDownload = async () => {
    const blob = await pdf(
      <InvoiceOverviewDocument
        invoiceData={invoiceData}
        logoBase64={logoBase64}
      />
    ).toBlob();

    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `INVOICE_${invoiceData.invoiceCode || "invoice"}.pdf`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <CustomButton
          text={
            <span>
              Download <FontAwesomeIcon icon={faDownload} />
            </span>
          }
          onClick={handleDownload}
        />
      </div>
      <div style={{ height: 20 }} />
      <PDFViewer width="100%" height="450px" showToolbar={false}>
        <InvoiceOverviewDocument
          invoiceData={invoiceData}
          logoBase64={logoBase64}
        />
      </PDFViewer>
    </div>
  );
};

export default InvoiceOverviewTemplate;
